import { UserType } from "./models";

export type JwtPayload = {
  sub: string;
  id: string;
  userType: UserType;
  scopes: Scopes[];
};
export type RefreshToken = JwtPayload & { iat: number };
export type AccessToken = RefreshToken & { exp: number };

export enum Scopes {
  // super admin roles
  SuperAdminRead = "superAdmin:read",
  SuperAdminWrite = "superAdmin:write",
  SuperAdminLogs = "superAdmin:logs",
  SuperAdminConfig = "superAdmin:config",
  SuperAdminPaymentRead = "superAdmin:payment:read",
  SuperAdminPaymentWrite = "superAdmin:payment:write",
  SuperAdminSyncAllStates = "superAdmin:syncAllStates",
  // user scopes
  ApplicationRead = "application:read",
  ApplicationWrite = "application:write",
  ApplicationKeyRead = "applicationKey:read",
  ApplicationKeyWrite = "applicationKey:write",
  OrganizationRead = "organization:read",
  OrganizationWrite = "organization:write",
  OrganizationDelete = "organization:delete",
  BillingWrite = "billing:write",
  BillingRead = "billing:read",
  PaymentWrite = "payment:write",
  PaymentRead = "payment:read",
  SubscriptionRead = "subscription:read",
  SubscriptionWrite = "subscription:write"
}

export enum Role {
  Owner = "Owner",
  Admin = "Admin",
  Developer = "Developer",
  Finance = "Finance"
}

export const Roles: Record<Role, Scopes[]> = {
  [Role.Owner]: [
    Scopes.ApplicationRead,
    Scopes.ApplicationWrite,
    Scopes.ApplicationKeyRead,
    Scopes.ApplicationKeyWrite,
    Scopes.OrganizationRead,
    Scopes.OrganizationWrite,
    Scopes.OrganizationDelete,
    Scopes.BillingWrite,
    Scopes.BillingRead,
    Scopes.PaymentWrite,
    Scopes.PaymentRead,
    Scopes.SubscriptionRead,
    Scopes.SubscriptionWrite
  ],
  [Role.Admin]: [
    Scopes.ApplicationRead,
    Scopes.ApplicationWrite,
    Scopes.ApplicationKeyRead,
    Scopes.ApplicationKeyWrite,
    Scopes.OrganizationRead,
    Scopes.OrganizationWrite,
    Scopes.SubscriptionRead
  ],
  [Role.Developer]: [
    Scopes.ApplicationRead,
    Scopes.ApplicationWrite,
    Scopes.ApplicationKeyRead,
    Scopes.ApplicationKeyWrite,
    Scopes.OrganizationRead,
    Scopes.SubscriptionRead,
    Scopes.PaymentRead
  ],
  [Role.Finance]: [
    Scopes.ApplicationRead,
    Scopes.ApplicationKeyRead,
    Scopes.OrganizationRead,
    Scopes.BillingWrite,
    Scopes.BillingRead,
    Scopes.PaymentWrite,
    Scopes.PaymentRead,
    Scopes.SubscriptionRead
  ]
};

export const hasScopes = (scopes: Scopes[], requiredScopes: Scopes[]) => {
  return requiredScopes.every((scope) => scopes.includes(scope));
};

export const hasRole = (role: Role, scopes: Scopes[]) => {
  return Roles[role].every((roleScope) => scopes.includes(roleScope));
};
const rolePriority: Role[] = [Role.Owner, Role.Admin, Role.Developer, Role.Finance];

export const determineRole = (scopes: Scopes[]): Role | null => {
  for (const role of rolePriority) {
    if (hasRole(role, scopes)) {
      return role;
    }
  }
  return null;
};

export const LOGIN_STATE_PARAM = "loginState";
export const LOGIN_STATE_COOKIE = "login-state";

export type LoginState = {
  redirectUrl?: string;
  promoCode?: string;
};
